import { inputTypesEnum, OptionItem } from '@kuiper/ibusiness-core';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  OnInit,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import {
  FormArray,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  TranslocoPipe,
  TranslocoService,
  translateSignal,
} from '@jsverse/transloco';
import { map, startWith } from 'rxjs';
import { ButtonComponent } from '../../../../../../ui-components/lib/button/button.component';
import { InputComponent } from '../../../../../../ui-components/lib/input/input.component';
import { SelectComponent } from '../../../../../../ui-components/lib/select/select.component';
import { MultiSelectComponent } from '../../../../../../ui-components/lib/multi-select/multi-select.component';
import { ToggleComponent } from '../../../../../../ui-components/lib/toggle/toggle.component';
import { ValidationMessageComponent } from '../../../../../../ui-components/lib/validation-message/validation-message.component';
import { ToggleOption } from '../../../../../../ui-components/lib/option.interface';
import { AttachmentTypeEnum } from '../../../../../../ui-components/lib/upload-files/attachment-type.enum';
import { FormService } from '../../../../../../form-builder/services/form/form.service';
import { DualLangInputComponent } from '../../../../../../form-builder/lib/dual-lang-input/dual-lang-input.component';
import { CustomWorkflowService } from '../custom-workflow.service';
import {
  CustomWorkflow,
  CustomWorkflowApiError,
  CustomWorkflowField,
  CustomWorkflowFieldOptions,
  localizedGroup,
  localizedOrNull,
  rawValueSignal,
  slugifyKey,
} from '../custom-workflow.model';
import {
  DEFAULT_EXTENSIONS,
  KEY_PATTERN,
  MAX_FILE_SIZE_VALIDATORS,
} from './custom-workflow-field-editor.constants';

@Component({
  selector: 'lib-custom-workflow-field-editor',
  imports: [
    ReactiveFormsModule,
    TranslocoPipe,
    InputComponent,
    DualLangInputComponent,
    SelectComponent,
    MultiSelectComponent,
    ToggleComponent,
    ButtonComponent,
    ValidationMessageComponent,
  ],
  templateUrl: './custom-workflow-field-editor.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CustomWorkflowFieldEditorComponent implements OnInit {
  inputTypesEnum = inputTypesEnum;
  readonly service = inject(CustomWorkflowService);
  private readonly formService = inject(FormService);
  private readonly transloco = inject(TranslocoService);
  private readonly destroyRef = inject(DestroyRef);

  workflow = input.required<CustomWorkflow>();
  sectionId = input.required<number>();
  field = input<CustomWorkflowField | null>(null);
  options = input.required<CustomWorkflowFieldOptions>();
  readonly = input<boolean>(false);
  saved = output<void>();
  cancelled = output<void>();
  apiError = output<CustomWorkflowApiError>();

  isSaving = signal(false);
  /**
   * The key we last auto-derived from the label, or null once the user has taken the
   * key over. Compared against the control's live value (not a "touched" flag) so this
   * survives both real typing and a patchValue() that sets key and label in one call —
   * lib-input's own ngOnInit calls updateValueAndValidity(), which re-emits valueChanges
   * with no value change, so a subscribe-based "was it touched" flag is not reliable here.
   */
  private lastAutoKey: string | null = '';
  private readonly lockedOptionCount = signal(0);

  form = new FormGroup({
    type: new FormControl('', {
      nonNullable: true,
      validators: Validators.required,
    }),
    key: new FormControl('', {
      nonNullable: true,
      validators: [
        Validators.required,
        Validators.maxLength(64),
        Validators.pattern(KEY_PATTERN),
      ],
    }),
    label: localizedGroup(),
    mandatory: new FormControl(true, { nonNullable: true }),
    hint: localizedGroup({}, { required: false }),
    placeholder: localizedGroup({}, { required: false }),
    attachment_type: new FormControl(AttachmentTypeEnum.ANY, {
      nonNullable: true,
    }),
    extensions: new FormControl<string[]>(DEFAULT_EXTENSIONS, {
      nonNullable: true,
      validators: Validators.required,
    }),
    max_file_size: new FormControl(10240, {
      nonNullable: true,
      validators: MAX_FILE_SIZE_VALIDATORS,
    }),
    lookup_model_config_id: new FormControl<number | null>(null),
    options: new FormArray<FormGroup>([]),
  });
  private readonly initial = signal('');
  private readonly formValue = rawValueSignal(this.form);
  dirty = computed(() => JSON.stringify(this.formValue()) !== this.initial());

  private readonly typeValue = toSignal(
    this.form.controls.type.valueChanges.pipe(
      startWith(this.form.controls.type.value)
    ),
    { initialValue: '' }
  );
  selectedType = computed(
    () => this.options().types.find(t => t.value === this.typeValue()) ?? null
  );
  typeOptions = computed(() =>
    this.options().types.map(t => {
      const key = `custom_workflow_field_type_${t.value}`;
      const translated = this.transloco.translate(key);
      return {
        label: translated === key ? t.label : translated,
        value: t.value,
      };
    })
  );
  /** A Variable Options field looks up a Variable's options, not a model. */
  lookupOptions = computed(() =>
    this.selectedType()?.value === inputTypesEnum.VARIABLE_OPTIONS
      ? this.options().lookup_variables.map(v => ({
          label: v.name,
          value: v.id,
        }))
      : this.options().lookup_models.map(m => ({ label: m.name, value: m.id }))
  );

  private readonly attachmentTypeValue = toSignal(
    this.form.controls.attachment_type.valueChanges.pipe(
      startWith(this.form.controls.attachment_type.value)
    ),
    { initialValue: AttachmentTypeEnum.ANY }
  );
  private readonly selectedAttachmentType = computed(() =>
    this.attachmentTypeFor(this.attachmentTypeValue())
  );
  attachmentTypeOptions = computed<OptionItem[]>(() =>
    (this.options().attachment_types ?? []).map(t => ({
      label: t.label,
      value: t.value,
    }))
  );
  /** The admin may only pick extensions the upload API accepts for the selected attachment type. */
  extensionOptions = computed(() =>
    (this.selectedAttachmentType()?.extensions ?? []).map(e => ({
      label: e,
      value: e,
    }))
  );
  maxFileSizeLimit = computed(
    () => this.selectedAttachmentType()?.max_file_size ?? null
  );

  private readonly optionCount = toSignal(
    this.form.controls.options.valueChanges.pipe(
      map(() => this.form.controls.options.length)
    ),
    {
      initialValue: 0,
    }
  );
  /** Option rows with their lock state, so the template calls nothing. */
  optionRows = computed(() => {
    this.optionCount();
    const locked = this.lockedOptionCount();
    return this.form.controls.options.controls.map((group, i) => ({
      group,
      removable: i >= locked,
    }));
  });
  private readonly requiredLabel = translateSignal(
    'custom_workflow_field_required_label'
  );
  mandatoryToggle = computed<ToggleOption>(() => ({
    label: this.requiredLabel() as string,
    value: true,
  }));

  ngOnInit() {
    const field = this.field();
    if (field) {
      this.patchFromField(field);
      if (this.workflow().locked) this.lockStructure();
    }
    this.form.controls.label.controls.en.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(label => {
        if (
          this.lastAutoKey === null ||
          this.form.controls.key.value !== this.lastAutoKey
        )
          return;
        this.lastAutoKey = slugifyKey(label);
        this.form.controls.key.setValue(this.lastAutoKey);
      });
    this.watchMediaSettings();
    if (this.readonly()) this.form.disable();
    this.initial.set(JSON.stringify(this.form.getRawValue()));
  }

  /**
   * Media settings are validated only for a media field, and always against the upload
   * limits of the selected attachment type.
   */
  private watchMediaSettings() {
    this.form.controls.type.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(type => this.toggleMediaControls(type));
    this.form.controls.attachment_type.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.applyAttachmentType());
    this.toggleMediaControls(this.form.controls.type.value);
    this.applyAttachmentType();
  }

  private toggleMediaControls(type: string) {
    const isMedia = !!this.options().types.find(t => t.value === type)
      ?.is_media;
    const { attachment_type, extensions, max_file_size } = this.form.controls;
    for (const control of [attachment_type, extensions, max_file_size]) {
      if (isMedia && !this.readonly()) control.enable({ emitEvent: false });
      else control.disable({ emitEvent: false });
    }
  }

  /** Caps the max file size at the attachment type's upload limit and drops the extensions it does not accept. */
  private applyAttachmentType() {
    const attachmentType = this.attachmentTypeFor(
      this.form.controls.attachment_type.value
    );
    if (!attachmentType) return;
    const { extensions, max_file_size } = this.form.controls;
    max_file_size.setValidators([
      ...MAX_FILE_SIZE_VALIDATORS,
      Validators.max(attachmentType.max_file_size),
    ]);
    max_file_size.updateValueAndValidity();
    extensions.setValue(
      extensions.value.filter(extension =>
        attachmentType.extensions.includes(extension)
      )
    );
  }

  private attachmentTypeFor(value: AttachmentTypeEnum) {
    return (
      this.options().attachment_types?.find(t => t.value === value) ?? null
    );
  }

  private patchFromField(field: CustomWorkflowField) {
    this.lastAutoKey = null;
    this.form.patchValue({
      type: field.type,
      key: field.key,
      label: {
        en: field.translations.label.en,
        ar: field.translations.label.ar,
      },
      mandatory: field.metadata.mandatory,
      hint: {
        en: field.metadata.hint?.en ?? '',
        ar: field.metadata.hint?.ar ?? '',
      },
      placeholder: {
        en: field.metadata.placeholder?.en ?? '',
        ar: field.metadata.placeholder?.ar ?? '',
      },
      attachment_type: field.metadata.attachment_type ?? AttachmentTypeEnum.ANY,
      extensions: (field.metadata.extensions ?? '').split(',').filter(Boolean),
      max_file_size: field.metadata.max_file_size,
      lookup_model_config_id: field.metadata.lookup_model_config_id,
    });
    field.metadata.options
      .filter(o => o.value)
      .forEach(o => this.addOption(o.value, o.label.en, o.label.ar));
  }

  /** A locked workflow already has requests: its key, type, lookup model and existing option values are frozen. */
  private lockStructure() {
    this.form.controls.key.disable();
    this.form.controls.type.disable();
    this.form.controls.lookup_model_config_id.disable();
    const options = this.form.controls.options;
    this.lockedOptionCount.set(options.length);
    for (let i = 0; i < options.length; i++)
      options.at(i).get('value')!.disable();
  }

  /** Bound to the key input's own `(typing)` — once the admin edits it directly, stop deriving it from the label. */
  markKeyTouched() {
    this.lastAutoKey = null;
  }

  addOption(value = '', en = '', ar = '') {
    this.form.controls.options.push(
      new FormGroup({
        value: new FormControl(value, {
          nonNullable: true,
          validators: [Validators.required, Validators.maxLength(100)],
        }),
        label: localizedGroup({ en, ar }),
      })
    );
  }

  canRemoveOption(index: number): boolean {
    return index >= this.lockedOptionCount();
  }

  removeOption(index: number) {
    if (this.canRemoveOption(index)) this.form.controls.options.removeAt(index);
  }

  submit() {
    this.formService.syncDualLangControls(this.form);
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    const raw = this.form.getRawValue();
    const type = this.selectedType();
    const body: Record<string, unknown> = {
      label: raw.label,
      section_id: this.sectionId(),
      mandatory: raw.mandatory,
      hint: localizedOrNull(raw.hint),
    };
    if (!this.form.controls.type.disabled) body['type'] = raw.type;
    if (!this.form.controls.key.disabled) body['key'] = raw.key;
    if (type?.has_placeholder)
      body['placeholder'] = localizedOrNull(raw.placeholder);
    if (type?.has_options) {
      body['options'] = raw.options.map((o: any) => ({
        value: o.value,
        label: o.label,
      }));
    }
    if (type?.is_media) {
      body['attachment_type'] = raw.attachment_type;
      body['extensions'] = raw.extensions.join(',');
      body['max_file_size'] = raw.max_file_size;
    }
    if (type?.is_lookup && !this.form.controls.lookup_model_config_id.disabled)
      body['lookup_model_config_id'] = raw.lookup_model_config_id;

    const field = this.field();
    const request = field
      ? this.service.updateField(this.workflow().id, field.id, body)
      : this.service.createField(this.workflow().id, body);

    this.isSaving.set(true);
    request.subscribe({
      next: () => {
        this.isSaving.set(false);
        this.saved.emit();
      },
      error: (error: CustomWorkflowApiError) => {
        this.isSaving.set(false);
        if (error?.errors) this.formService.addControlErrors(error, this.form);
        this.apiError.emit(error);
      },
    });
  }
}
